import { Drawer as BaseDrawer } from "@base-ui/react/drawer";
import clsx from "clsx";
import React, { forwardRef, useEffect, useState } from "react";
import { IconClose } from "../icons";
import {
  drawerBodyClass,
  drawerContentClass,
  drawerFooterClass,
  drawerHeaderClass,
  drawerOverlayClass,
  drawerTitlesClass,
  drawerViewportClass,
} from "./styles.css";

export type DrawerSide = "left" | "right" | "top" | "bottom";

const swipeDirectionMap: Record<DrawerSide, "left" | "right" | "up" | "down"> =
  {
    left: "left",
    right: "right",
    top: "up",
    bottom: "down",
  };

type DrawerProps = Omit<React.HTMLAttributes<HTMLDivElement>, "open"> & {
  open?: boolean;
  toggle?: () => void;
  side?: DrawerSide;
  overlayStyles?: React.CSSProperties;
  disableSwipe?: boolean;
};

const DrawerRoot = forwardRef(function DrawerRoot(
  {
    open,
    toggle,
    side = "bottom",
    overlayStyles,
    disableSwipe = false,
    children,
    className,
    ...props
  }: DrawerProps,
  ref: React.Ref<HTMLDivElement>,
) {
  // Keep the last side used while open so the exit animation plays from
  // the correct direction even if the consumer resets the side prop on close.
  const [lockedSide, setLockedSide] = useState<DrawerSide>(side);
  useEffect(() => {
    if (open) setLockedSide(side);
  }, [open, side]);
  const activeSide = open ? side : lockedSide;

  const modifiedChildren = React.Children.map(
    children as React.ReactNode,
    (child: React.ReactNode) => {
      if (
        React.isValidElement(child) &&
        (child.props as Record<string, unknown>).hasClose
      ) {
        return React.cloneElement(
          child as React.ReactElement<{ toggle?: () => void }>,
          { toggle },
        );
      }
      return child;
    },
  );

  return (
    <BaseDrawer.Root
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) toggle?.();
      }}
      {...(!disableSwipe && { swipeDirection: swipeDirectionMap[activeSide] })}
    >
      <BaseDrawer.Portal>
        <BaseDrawer.Backdrop
          className={drawerOverlayClass}
          style={overlayStyles}
          data-slot="drawer-overlay"
        />
        <BaseDrawer.Viewport className={drawerViewportClass}>
          <BaseDrawer.Popup
            ref={ref}
            className={clsx(drawerContentClass, className)}
            data-side={activeSide}
            data-slot="drawer-content"
            {...props}
          >
            {modifiedChildren}
          </BaseDrawer.Popup>
        </BaseDrawer.Viewport>
      </BaseDrawer.Portal>
    </BaseDrawer.Root>
  );
});

interface DrawerHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  toggle?: () => void;
  hasClose?: boolean;
}

const Header = ({
  children,
  hasClose,
  toggle,
  className,
  ...props
}: DrawerHeaderProps) => {
  return (
    <div
      className={clsx(drawerHeaderClass, className)}
      data-slot="drawer-header"
      {...props}
    >
      <div className={drawerTitlesClass} data-slot="drawer-titles">
        {children}
      </div>
      {hasClose && <IconClose onClick={() => toggle?.()} />}
    </div>
  );
};

interface DrawerBodyProps extends React.HTMLAttributes<HTMLDivElement> {}

const Body = ({ children, className, ...props }: DrawerBodyProps) => {
  return (
    <div
      className={clsx(drawerBodyClass, className)}
      data-slot="drawer-body"
      {...props}
    >
      {children}
    </div>
  );
};

interface DrawerFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

const Footer = ({ children, className, ...props }: DrawerFooterProps) => {
  return (
    <div
      className={clsx(drawerFooterClass, className)}
      data-slot="drawer-footer"
      {...props}
    >
      {children}
    </div>
  );
};

const DrawerComponent = Object.assign(DrawerRoot, {
  Header,
  Body,
  Footer,
});

export { DrawerComponent as Drawer };

DrawerRoot.displayName = "DrawerRoot";
